import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useHistory } from "react-router-dom";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import useBasePath from "src/utils/hooks/useBasePath";

import {
  ICalendar,
  ICalendarStatusArgs,
  IExcludedDay,
  INewCalendar,
} from "src/Pages/Company/Absences/Calendars/Calendars.models";
import { IFormValues } from "src/Pages/Company/Absences/Calendars/Calendar/CalendarForm";
import {
  changeStatusCalendar,
  createCalendar,
  deleteCalendar,
  editCalendar,
  fetchCalendar,
  fetchCalendars,
  fetchEmployeeCalendars,
  fetchPublicHolidays,
} from "./api";
import { AbsenceQueryKeys } from "./config";

interface ISaveCalendarMutation {
  companyId: string;
  calendarId?: string;
  isNew: boolean;
}

const CalendarsQueries = {
  list: (companyId: string, years: string[] = []) =>
    queryOptions<ICalendar[], Error>({
      queryKey: AbsenceQueryKeys.calendars(companyId),
      queryFn: ({ signal }) => fetchCalendars({ companyId, years, signal }),
      initialData: [],
    }),

  details: (calendarId: string) =>
    queryOptions<ICalendar, Error>({
      enabled: !!calendarId,
      queryKey: AbsenceQueryKeys.calendar(calendarId),
      queryFn: ({ signal }) => fetchCalendar({ calendarId, signal }),
    }),

  employeeCalendars: (params: {
    companyId: string;
    employeeId: string | undefined;
    years?: string[];
  }) =>
    queryOptions<ICalendar[], Error>({
      enabled: !!params.employeeId,
      queryKey: AbsenceQueryKeys.employeeCalendars(
        params.companyId,
        params.employeeId!,
        params.years ?? []
      ),
      queryFn: ({ signal }) =>
        fetchEmployeeCalendars({
          companyId: params.companyId,
          employeeId: params.employeeId!,
          years: params.years ?? [],
          signal,
        }),
    }),

  delete: (companyId: string) =>
    mutationOptions({
      mutationFn: ({ calendarId, eTag }: Omit<ICalendarStatusArgs, "active">) =>
        deleteCalendar(calendarId, eTag),
    }),

  changeStatus: mutationOptions<unknown, AxiosError, ICalendarStatusArgs>({
    mutationFn: ({ calendarId, active, eTag }) =>
      changeStatusCalendar(calendarId, active, eTag),
  }),

  publicHolidays: (from: string, to: string) =>
    queryOptions<IExcludedDay[], Error>({
      enabled: false,
      queryKey: AbsenceQueryKeys.publicHolidays(from, to),
      queryFn: () => fetchPublicHolidays(from, to),
    }),
};

export const useFetchCalendars = (companyId: string, years?: string[]) =>
  useQuery(CalendarsQueries.list(companyId, years));

export const useFetchCalendar = (calendarId: string) =>
  useQuery(CalendarsQueries.details(calendarId));

export const useFetchEmployeeCalendars = (params: {
  companyId: string;
  employeeId: string | undefined;
  years?: string[];
}) => useQuery(CalendarsQueries.employeeCalendars(params));

export const useFetchPublicHolidays = (from: string, to: string) =>
  useQuery(CalendarsQueries.publicHolidays(from, to));

export const useSaveCalendar = ({
  companyId,
  calendarId,
  isNew,
}: ISaveCalendarMutation) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const basePath = useBasePath();

  return useMutation({
    mutationFn: async ({
      formData,
      excludedDays = [],
    }: {
      formData: IFormValues;
      excludedDays: IExcludedDay[];
    }) => {
      const parsedFormData: INewCalendar | Omit<ICalendar, "eTag"> = {
        ...formData,
        ownerId: companyId,
        excludedDays,
      };

      if (!isNew) {
        parsedFormData.id = calendarId;
      }

      return isNew
        ? createCalendar(parsedFormData)
        : editCalendar(calendarId!, parsedFormData as ICalendar);
    },
    onSuccess: () => {
      dynamicNotification(__("Pomyślnie zapisano kalendarz."));
      if (isNew) {
        setTimeout(() => {
          history.push(`${basePath}/absences/calendars`);
        }, 500);
      } else {
        queryClient.invalidateQueries({
          queryKey: AbsenceQueryKeys.calendar(calendarId!),
        });
      }
    },
    onError: () => {
      dynamicNotification(
        __("Nie udało się zapisać danych."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useDeleteCalendar = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...CalendarsQueries.delete(companyId),
    onSuccess: () => {
      dynamicNotification(__("Kalendarz został usunięty."), "success");
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.calendars(companyId),
      });
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się usunąć kalendarza."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useChangeStatusCalendar = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError, ICalendarStatusArgs>({
    ...CalendarsQueries.changeStatus,
    onSuccess: () => {
      dynamicNotification(__("Kalendarz został dezaktywowany."), "success");
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.calendars(companyId),
      });
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Kalendarz nie został dezaktywowany."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export default CalendarsQueries;
